"use client";

import { usePathname } from "next/navigation";
import Script from "next/script";
import React, { useEffect, useRef } from "react";

import useGoogleMap from "@/hooks/useGoogleMap";
import { Map } from "@/types";

import "./Map.css";
import countryPlaceIdMapping from "./config/countryPlaceIdMapping";
import { MapComponentProps } from "./type";

export type CountryCode = "AE" | "US" | "JD" | "LB" | "QR" | "MV" | "GB" | "SA";

export const countryCoordinates: Record<CountryCode, { lat: number; lng: number }> = {
  AE: { lat: 24.4539, lng: 54.3773 }, // United Arab Emirates
  US: { lat: 37.0902, lng: -95.7129 }, // United States
  JD: { lat: 30.5852, lng: 36.2384 }, // Jordan
  LB: { lat: 33.8547, lng: 35.8623 }, // Lebanon
  QR: { lat: 25.3548, lng: 51.1839 }, // Qatar
  MV: { lat: 3.2028, lng: 73.2207 }, // Maldives
  GB: { lat: 55.3781, lng: -3.436 }, // United Kingdom
  SA: { lat: 23.8859, lng: 45.0792 }, // Saudi Arabia
};
const getPlaceIdsForCountries = (countries: string[]) => {
  const placeIds = countries.map((country) => countryPlaceIdMapping[country]).filter((id) => id);
  return placeIds;
};

interface MapComponentPropsExtended extends MapComponentProps {
  entry: Map;
  selectedTabIndex: number;
  mapOptions?: google.maps.MapOptions;
  googleMapApiKey: string;
  onMapInit?: Function;
}

const MapComponent: React.FC<MapComponentPropsExtended> = ({ entry, selectedTabIndex = 0, mapOptions, googleMapApiKey, onMapInit }) => {
  const pathname = usePathname();
  const mapRef = useRef<HTMLDivElement>(null);

  let center = { lat: Math.round(entry.center.lat), lng: Math.round(entry.center.lon) };

  const { map, setScriptLoaded } = useGoogleMap(mapRef, center, entry.zoom, { ...mapOptions });

  useEffect(() => {
    if (map) {
      map.data.forEach((feature) => {
        map.data.remove(feature);
      });

      const desiredPlaceIds = getPlaceIdsForCountries(entry.tabs[selectedTabIndex].countries);

      const featureLayer = map.getFeatureLayer(google.maps.FeatureType.COUNTRY);

      const featureStyleOptions: google.maps.FeatureStyleOptions = {
        strokeColor: "#AEB8C2",
        strokeOpacity: 1.0,
        strokeWeight: 2.0,
        fillColor: "#E8DBBF",
        fillOpacity: 1.0,
      };

      //@ts-ignore
      featureLayer.style = (options: { feature: { placeId: string } }) => {
        if (desiredPlaceIds.includes(options.feature.placeId)) {
          return featureStyleOptions;
        }
      };
      if (pathname.includes("global-presence-properties")) {
        const selectedTab = entry.tabs[selectedTabIndex];
        const countryCode = selectedTab.countries[0];
        const center = countryCode && countryCoordinates.hasOwnProperty(countryCode) ? countryCoordinates[countryCode as CountryCode] : undefined;
        console.log(selectedTab.countries?.length, "selectedTab haha");
        if (center && selectedTab.countries?.length === 1) {
          map.setCenter(center);
          map.setZoom(
            countryCode === "MV" || countryCode === "JD" || countryCode === "LB" || countryCode === "QR" || countryCode === "AE" ? 6 : entry.zoom,
          );
        } else if (selectedTab.countries?.length > 1) {
          map.setZoom(entry.zoom);
        }
      }
    }
  }, [map, selectedTabIndex]);

  useEffect(() => {
    if (map && onMapInit) {
      onMapInit(map);
    }
  }, [map]);
  return (
    <div className="grow" style={{ color: "#222222" }}>
      <div className={`min-h-[400px] h-[calc(100dvh_-_50px)] md:h-[626px] ${entry.interactive ? "" : "disable-interaction"}`} id="map" ref={mapRef}>
        <Script
          onReady={() => {
            setScriptLoaded(true);
          }}
          id="google-maps"
          strategy="lazyOnload"
          src={`https://maps.googleapis.com/maps/api/js?key=${googleMapApiKey}&libraries=places,marker&v=quarterly`}
        />
      </div>
    </div>
  );
};

export default MapComponent;
